import React, { useState } from 'react';
import styled from 'styled-components';
import { useComputed, useSignalEffect } from '@preact/signals-react';
import { ChatMessages } from '@/features/ai-agents/chat/chat-messages';
import {
  ChatInput,
  ChatInputHandle,
} from '@/features/ai-agents/components/chat-input';
import { useFileDropDetector } from '@/features/ai-agents/shared/use-file-drop-detector';
import { BackdropSpinner } from '@/components/backdrop-spinner';
import { useChatSession } from '@/features/ai-agents/chat/context';
import { AiConversationStarter } from '@/features/ai-agents/chat/starters';
import { FollowupSuggestions } from '@/features/ai-agents/chat/followup-suggestions';
import { useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';
import { extractRecommendations } from '@/features/ai-agents/chat/recommendations';
import { ContextualAttachment } from '@/features/ai-agents/shared/models';

export interface ChatAreaProps {
  enableFilesUpload?: boolean;
  enableExamples?: boolean;
  enableVoiceInput?: boolean;
  showReasoning?: boolean;
  showTools?: boolean;

  inputBottom?: string;
  inputLeft?: string;
  inputRight?: string;

  starters?: AiConversationStarter;
  initialContextualAttachments?: ContextualAttachment[];
}

export function ChatArea({
  enableFilesUpload,
  enableExamples,
  enableVoiceInput,
  showReasoning,
  showTools,
  inputBottom,
  inputLeft,
  inputRight,
  starters,
  initialContextualAttachments,
}: ChatAreaProps) {
  const dropzoneVisible = useFileDropDetector();
  const {
    agent: selectedAgent,
    isLoadingSession,
    sendMessage: sendUserMessage,
    isProcessing,
    abortMessage,
    loadSession,
    currentSessionId,
    previousSessionId,
    messages,
  } = useChatSession();

  useSignalEffect(() => {
    if (
      currentSessionId.value !== previousSessionId.value &&
      !isLoadingSession.peek()
    ) {
      loadSession().then();
    }
  });

  const [isScrollButtonsShown, setIsScrollButtonsShown] = useState(false);

  const showStarters = useComputed(
    () => messages?.value?.length <= 0 && !!starters,
  );

  const chatRef = React.useRef<ChatInputHandle>(null);

  const starterSendMessage = (prompt: string, sendPrompt = true) => {
    chatRef.current?.setPrompt(prompt);
    if (sendPrompt) {
      setTimeout(() => {
        chatRef.current?.send();
      }, 100);
    }
  };

  const starterAttachFile = () => {
    chatRef.current?.openDropzone();
  };

  const starterAttachSketch = () => {
    chatRef.current?.openWhiteboard();
  };

  const followupsEnabled = useFeatureBit(FeatureBit.SHOW_AI_FOLLOWUPS);

  const recommendations = useComputed(() => {
    if (!isProcessing.value) {
      const msgs = messages.value;
      if (!msgs || msgs.length === 0) return [];
      const lastMsg = msgs[msgs.length - 1];
      if (lastMsg.role !== `agent`) return [];
      return extractRecommendations(lastMsg.content) || [];
    }
    return [];
  });

  const onSelectRecommendation = (prompt: string) => {
    starterSendMessage(prompt, true);
  };

  const onScrollStateChange = (isAtBottom: boolean) => {
    if (isAtBottom) {
      setIsScrollButtonsShown(false);
    } else {
      setIsScrollButtonsShown(true);
    }
  };

  return (
    <Wrapper>
      {isLoadingSession.value && (
        <BackdropSpinner
          loading
          blurAmount={0}
          spinnerSize={40}
          backgroundColor="rgba(0,0,0,0)"
        />
      )}
      {showStarters.value &&
        starters?.(starterSendMessage, starterAttachFile, starterAttachSketch)}
      {!showStarters.value && (
        <ChatMessages
          showTools={showTools}
          showReasoning={showReasoning}
          onScrollStateChange={onScrollStateChange}
        />
      )}
      <InputWrapper
        inputBottom={inputBottom}
        inputLeft={inputLeft}
        inputRight={inputRight}
      >
        {followupsEnabled &&
          !isScrollButtonsShown &&
          recommendations.value.length > 0 && (
            <FollowupSuggestions
              recommendations={recommendations.value}
              onSelect={onSelectRecommendation}
            />
          )}
        <ChatInput
          ref={chatRef}
          disabled={!(selectedAgent || isLoadingSession.value)}
          showDropZone={dropzoneVisible}
          onSubmit={sendUserMessage}
          onAbort={abortMessage}
          loading={isProcessing.value}
          enableFilesUpload={enableFilesUpload}
          enableExamples={enableExamples}
          enableVoiceInput={enableVoiceInput}
          initialContextualAttachments={initialContextualAttachments}
        />
      </InputWrapper>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const InputWrapper = styled.div`
  position: absolute;
  bottom: ${(p) => p.inputBottom || `3rem`};
  left: ${(p) => p.inputLeft || `0`};
  right: ${(p) => p.inputRight || `0`};
`;
